import { alpha } from "@mui/material/styles"
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material"
import { Mail, Phone, Plus, Unlink, UserPlus, Users } from "lucide-react"
import { useCallback, useState, type JSX } from "react"

import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useToast } from "@/components/ui/use-toast"
import { LinkCustomerToResellerModal } from "@crm-src/components/link-customer-to-reseller-modal"
import { LinkProspectToResellerModal } from "@crm-src/components/link-prospect-to-reseller-modal"
import { ResellerDealersList } from "@crm-src/components/reseller-dealers-list"
import { ResellerNetworkColumn } from "@crm-src/components/reseller-network-column"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useResellerPagedList } from "@crm-src/hooks/useResellerPagedList"
import { customersApi } from "@crm-src/lib/customers-api"
import { leadsApi } from "@crm-src/lib/leads-api"
import { resellersApiService, type ResellerNetworkRow } from "@crm-src/lib/resellers-api"

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

/**
 * The Network tab — Dealers ∣ Customers ∣ Prospects, SIDE BY SIDE.
 *
 * Three columns, three independent queries. Each one searches and pages on its
 * own, server-side: the old page fetched every customer and prospect in one
 * unpaginated array and split them in the browser, so a reseller with a few
 * hundred customers rendered a few hundred rows into two scroll boxes with no
 * way to find anything in them.
 *
 * The columns stack below `lg`, and each keeps its search box and pager
 * attached to its own list rather than sharing one bar — a shared control would
 * have to mean "all three", which is not a thing anyone wants to do here.
 */
export function ResellerNetworkTab({ resellerId }: { resellerId: string }): JSX.Element {
  const { toast } = useToast()
  const { isAdmin } = usePermissions()

  const [isLinkCustomerModalOpen, setIsLinkCustomerModalOpen] = useState(false)
  const [isLinkProspectModalOpen, setIsLinkProspectModalOpen] = useState(false)
  const [pendingUnlinkCustomer, setPendingUnlinkCustomer] = useState<ResellerNetworkRow | null>(null)
  const [pendingUnlinkProspect, setPendingUnlinkProspect] = useState<ResellerNetworkRow | null>(null)

  const customers = useResellerPagedList<ResellerNetworkRow>(
    useCallback(
      async ({ page, perPage, search }) => {
        const result = await resellersApiService.getNetwork(resellerId, {
          kind: "customer",
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
        })
        return {
          rows: result.customers,
          totalCount: result.pagination.total_count,
          totalPages: result.pagination.total_pages,
        }
      },
      [resellerId],
    ),
  )

  const prospects = useResellerPagedList<ResellerNetworkRow>(
    useCallback(
      async ({ page, perPage, search }) => {
        const result = await resellersApiService.getNetwork(resellerId, {
          kind: "prospect",
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
        })
        return {
          rows: result.customers,
          totalCount: result.pagination.total_count,
          totalPages: result.pagination.total_pages,
        }
      },
      [resellerId],
    ),
  )

  const unlink = async (
    row: ResellerNetworkRow,
    kind: "customer" | "prospect",
  ): Promise<void> => {
    try {
      if (kind === "customer") await customersApi.bulkAssignReseller([row.id], "none")
      else await leadsApi.bulkAssignReseller([row.id], "none")

      toast({ title: "Success", description: `${kind === "customer" ? "Customer" : "Prospect"} unlinked from reseller` })
      // Only the affected column reloads — the other two keep their page and
      // their search term, which is the point of holding three query states.
      ;(kind === "customer" ? customers : prospects).refresh()
    } catch (error) {
      const message =
        (error as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0] ||
        `Failed to unlink ${kind}`
      toast({ variant: "destructive", title: "Error", description: message })
    }
  }

  const renderPerson = (
    row: ResellerNetworkRow,
    kind: "customer" | "prospect",
    onUnlink: (row: ResellerNetworkRow) => void,
  ): JSX.Element => {
    const isCustomer = kind === "customer"
    const badgeColor = isCustomer ? "success" : "warning"

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          p: 1.5,
          bgcolor: "surfaceSoft",
          borderRadius: 1,
          transition: "background-color 150ms",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (t) => alpha(t.palette[badgeColor].main, 0.12),
              color: `${badgeColor}.dark`,
              flexShrink: 0,
            }}
          >
            {isCustomer ? <Users size={16} /> : <UserPlus size={16} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h4" sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>
              {row.company_name || row.full_name}
            </Typography>
            {row.company_name && row.full_name && (
              <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500, mt: 0.25 }}>
                Contact: {row.full_name}
              </Typography>
            )}
            {row.email && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                <Mail size={12} />
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row.email}
                </Box>
              </Box>
            )}
            {row.phone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                <Phone size={12} />
                <span>{row.phone}</span>
              </Box>
            )}
            <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.5 }}>
              Added {formatDate(row.created_at)}
            </Typography>
          </Box>
        </Box>

        {isAdmin() && (
          <Tooltip title={`Unlink this ${kind} from the reseller`}>
            <IconButton
              size="small"
              aria-label={`Unlink ${row.company_name || row.full_name}`}
              sx={{ width: 28, height: 28, color: "text.secondary", "&:hover": { color: "error.main" }, flexShrink: 0 }}
              onClick={() => onUnlink(row)}
            >
              <Unlink size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    )
  }

  return (
    <>
      <Box
        data-cy="reseller-network-columns"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(3, 1fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <ResellerDealersList resellerId={resellerId} />

        <ResellerNetworkColumn<ResellerNetworkRow>
          data-cy="reseller-customers-column"
          title="Customers"
          icon={<Users size={18} />}
          entityNoun="customers"
          emptyMessage="No customers associated with this reseller"
          list={customers}
          {...(isAdmin()
            ? {
                action: (
                  <Tooltip describeChild title="Link existing customers to this reseller">
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      startIcon={<Plus size={12} />}
                      onClick={() => setIsLinkCustomerModalOpen(true)}
                    >
                      Link Customer
                    </Button>
                  </Tooltip>
                ),
              }
            : {})}
          renderRow={(row) => renderPerson(row, "customer", setPendingUnlinkCustomer)}
        />

        <ResellerNetworkColumn<ResellerNetworkRow>
          data-cy="reseller-prospects-column"
          title="Prospects"
          icon={<UserPlus size={18} />}
          entityNoun="prospects"
          emptyMessage="No prospects associated with this reseller"
          list={prospects}
          {...(isAdmin()
            ? {
                action: (
                  <Tooltip describeChild title="Link existing prospects to this reseller">
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      startIcon={<Plus size={12} />}
                      onClick={() => setIsLinkProspectModalOpen(true)}
                    >
                      Link Prospect
                    </Button>
                  </Tooltip>
                ),
              }
            : {})}
          renderRow={(row) => renderPerson(row, "prospect", setPendingUnlinkProspect)}
        />
      </Box>

      <ConfirmDialog
        isOpen={pendingUnlinkCustomer !== null}
        onClose={() => setPendingUnlinkCustomer(null)}
        title="Unlink Customer"
        variant="destructive"
        confirmLabel="Unlink"
        description={
          pendingUnlinkCustomer
            ? `Are you sure you want to unlink ${pendingUnlinkCustomer.company_name || pendingUnlinkCustomer.full_name} from this reseller? This will remove the association but will not delete the customer.`
            : ""
        }
        onConfirm={async () => {
          if (pendingUnlinkCustomer) await unlink(pendingUnlinkCustomer, "customer")
        }}
      />

      <ConfirmDialog
        isOpen={pendingUnlinkProspect !== null}
        onClose={() => setPendingUnlinkProspect(null)}
        title="Unlink Prospect"
        variant="destructive"
        confirmLabel="Unlink"
        description={
          pendingUnlinkProspect
            ? `Are you sure you want to unlink ${pendingUnlinkProspect.company_name || pendingUnlinkProspect.full_name} from this reseller? This will remove the association but will not delete the prospect.`
            : ""
        }
        onConfirm={async () => {
          if (pendingUnlinkProspect) await unlink(pendingUnlinkProspect, "prospect")
        }}
      />

      <LinkCustomerToResellerModal
        isOpen={isLinkCustomerModalOpen}
        onClose={() => setIsLinkCustomerModalOpen(false)}
        resellerId={resellerId}
        onSuccess={() => customers.refresh()}
        excludeCustomerIds={customers.rows.map((c) => c.id)}
      />

      <LinkProspectToResellerModal
        isOpen={isLinkProspectModalOpen}
        onClose={() => setIsLinkProspectModalOpen(false)}
        resellerId={resellerId}
        onSuccess={() => prospects.refresh()}
        excludeProspectIds={prospects.rows.map((p) => p.id)}
      />
    </>
  )
}
